/**
 * Widget Configuration
 * Defines all available widget types for the dashboard platform
 */

import {
  FileText,
  BarChart3,
  Target,
  CheckSquare,
  Calendar,
  StickyNote,
  Globe,
  Link2,
  Image,
  Clock,
  Activity,
  ExternalLink,
  Code,
  Cloud,
  DollarSign,
  Building2,
  Shield,
  Server,
  Ticket,
  Users,
  Eye,
  HardDrive,
  Building,
  LayoutGrid,
  FolderKanban,
  Briefcase,
  LayoutDashboard,
} from 'lucide-react';

// Widget type definitions
export const WIDGET_TYPES = {
  // Content widgets
  CUSTOM_TEXT: 'custom_text',
  CUSTOM_NOTES: 'custom_notes',
  IMAGE_WIDGET: 'image_widget',
  
  // Metric widgets
  CUSTOM_METRIC: 'custom_metric',
  CUSTOM_PROGRESS: 'custom_progress',
  CUSTOM_API_METRIC: 'custom_api_metric',
  AZURE_COST_WIDGET: 'azure_cost_widget',
  EXECUTIVE_CARD_WIDGET: 'executive_card_widget',
  DASHBOARD_LINK_WIDGET: 'dashboard_link_widget',
  
  // Microsoft/Azure widgets (auto-fetch using SSO)
  M365_OVERVIEW_WIDGET: 'm365_overview_widget',  // Hero widget - combined overview
  MS_LICENSES_WIDGET: 'ms_licenses_widget',
  MS_USERS_WIDGET: 'ms_users_widget',
  AZURE_RESOURCES_WIDGET: 'azure_resources_widget',
  SECURITY_POSTURE_WIDGET: 'security_posture_widget',
  SERVICE_HEALTH_WIDGET: 'service_health_widget',
  STORAGE_USAGE_WIDGET: 'storage_usage_widget',
  TENANT_INFO_WIDGET: 'tenant_info_widget',
  
  // IT Operations widgets
  PROJECTS_WIDGET: 'projects_widget',
  INTERNAL_SERVICES_WIDGET: 'internal_services_widget',
  EXTERNAL_SERVICES_WIDGET: 'external_services_widget',
  JIRA_OPERATIONS_WIDGET: 'jira_operations_widget',
  GOALS_TRACKER_WIDGET: 'goals_tracker_widget',
  EXPENSES_WIDGET: 'expenses_widget',
  
  // Productivity widgets
  CUSTOM_CHECKLIST: 'custom_checklist',
  CUSTOM_COUNTDOWN: 'custom_countdown',
  QUICK_LINKS: 'quick_links',
  
  // Embed widgets
  POWERBI_REPORT: 'powerbi_report',
  EMBED_WEBSITE: 'embed_website',
  JIRA_DASHBOARD_SNAPSHOT: 'jira_dashboard_snapshot',
};

// Widget catalog with metadata
export const WIDGET_CATALOG = {
  // === CONTENT WIDGETS ===
  [WIDGET_TYPES.CUSTOM_TEXT]: {
    id: WIDGET_TYPES.CUSTOM_TEXT,
    name: 'Text Block',
    description: 'Add custom text, notes, or information',
    icon: FileText,
    category: 'content',
    defaultSize: { w: 4, h: 2, minW: 2, minH: 2, maxW: 12, maxH: 6 },
    requiresApi: false,
    configurable: true,
  },
  [WIDGET_TYPES.CUSTOM_NOTES]: {
    id: WIDGET_TYPES.CUSTOM_NOTES,
    name: 'Sticky Notes',
    description: 'Quick notes and reminders',
    icon: StickyNote,
    category: 'content',
    defaultSize: { w: 3, h: 3, minW: 2, minH: 2, maxW: 6, maxH: 6 },
    requiresApi: false,
    configurable: true,
  },
  [WIDGET_TYPES.IMAGE_WIDGET]: {
    id: WIDGET_TYPES.IMAGE_WIDGET,
    name: 'Image / Logo',
    description: 'Display an image or company logo',
    icon: Image,
    category: 'content',
    defaultSize: { w: 3, h: 3, minW: 2, minH: 2, maxW: 6, maxH: 6 },
    requiresApi: false,
    configurable: true,
  },

  // === METRIC WIDGETS ===
  [WIDGET_TYPES.CUSTOM_METRIC]: {
    id: WIDGET_TYPES.CUSTOM_METRIC,
    name: 'Metric Card',
    description: 'Display a custom number or KPI manually entered',
    icon: BarChart3,
    category: 'metrics',
    defaultSize: { w: 2, h: 2, minW: 2, minH: 2, maxW: 4, maxH: 3 },
    requiresApi: false,
    configurable: true,
  },
  [WIDGET_TYPES.CUSTOM_PROGRESS]: {
    id: WIDGET_TYPES.CUSTOM_PROGRESS,
    name: 'Progress Tracker',
    description: 'Track progress toward a goal with percentage',
    icon: Target,
    category: 'metrics',
    defaultSize: { w: 3, h: 2, minW: 2, minH: 2, maxW: 6, maxH: 4 },
    requiresApi: false,
    configurable: true,
  },
  [WIDGET_TYPES.CUSTOM_API_METRIC]: {
    id: WIDGET_TYPES.CUSTOM_API_METRIC,
    name: 'API Metric',
    description: 'Fetch and display data from your own API endpoint',
    icon: Activity,
    category: 'metrics',
    defaultSize: { w: 3, h: 2, minW: 2, minH: 2, maxW: 6, maxH: 4 },
    requiresApi: true,
    configurable: true,
    helpText: 'Enter your API URL. The response should be JSON with a "value" field, or specify a JSON path to extract the value.',
  },
  [WIDGET_TYPES.EXECUTIVE_CARD_WIDGET]: {
    id: WIDGET_TYPES.EXECUTIVE_CARD_WIDGET,
    name: 'Executive Card',
    description: 'Configurable widget showing selected IT metrics from the executive dashboard',
    icon: Briefcase,
    category: 'metrics',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3, maxW: 12, maxH: 8 },
    requiresApi: true,
    configurable: true,
    featured: true,
    helpText: 'Select which metrics to display: costs, service health, security score, users, projects, and more.',
  },

  // === MICROSOFT & AZURE WIDGETS ===
  [WIDGET_TYPES.M365_OVERVIEW_WIDGET]: {
    id: WIDGET_TYPES.M365_OVERVIEW_WIDGET,
    name: 'M365 Overview',
    description: 'Combined dashboard showing licenses, users, security, and service health at a glance',
    icon: LayoutGrid,
    category: 'microsoft',
    defaultSize: { w: 8, h: 4, minW: 6, minH: 3, maxW: 12, maxH: 6 },
    requiresApi: true,
    configurable: false,
    helpText: 'Hero widget showing key M365 metrics: licenses, users, security posture, and service health in one view.',
    featured: true,
  },
  [WIDGET_TYPES.AZURE_COST_WIDGET]: {
    id: WIDGET_TYPES.AZURE_COST_WIDGET,
    name: 'Azure Costs',
    description: 'View Azure costs across all subscriptions and management groups (uses your Microsoft login)',
    icon: Cloud,
    category: 'microsoft',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3, maxW: 12, maxH: 8 },
    requiresApi: true,
    configurable: false,
    helpText: 'Automatically uses your Microsoft credentials to show Azure costs. No configuration needed.',
  },
  [WIDGET_TYPES.DASHBOARD_LINK_WIDGET]: {
    id: WIDGET_TYPES.DASHBOARD_LINK_WIDGET,
    name: 'Dashboard Link',
    description: 'Add a link to another user\'s dashboard',
    icon: LayoutDashboard,
    category: 'content',
    defaultSize: { w: 3, h: 3, minW: 2, minH: 2, maxW: 6, maxH: 4 },
    requiresApi: false,
    configurable: true,
    helpText: 'Create a clickable link to view another dashboard. Great for linking team dashboards together.',
  },
  [WIDGET_TYPES.MS_LICENSES_WIDGET]: {
    id: WIDGET_TYPES.MS_LICENSES_WIDGET,
    name: 'M365 Licenses',
    description: 'View Microsoft 365 license usage and availability (uses your Microsoft login)',
    icon: Building2,
    category: 'microsoft',
    defaultSize: { w: 6, h: 3, minW: 4, minH: 2, maxW: 12, maxH: 6 },
    requiresApi: true,
    configurable: false,
    helpText: 'Automatically uses your Microsoft credentials to show M365 license data.',
  },
  [WIDGET_TYPES.MS_USERS_WIDGET]: {
    id: WIDGET_TYPES.MS_USERS_WIDGET,
    name: 'Entra ID Users',
    description: 'View Azure AD/Entra ID user statistics (uses your Microsoft login)',
    icon: Users,
    category: 'microsoft',
    defaultSize: { w: 3, h: 2, minW: 2, minH: 2, maxW: 6, maxH: 4 },
    requiresApi: true,
    configurable: false,
    helpText: 'Automatically uses your Microsoft credentials to show user statistics.',
  },
  [WIDGET_TYPES.AZURE_RESOURCES_WIDGET]: {
    id: WIDGET_TYPES.AZURE_RESOURCES_WIDGET,
    name: 'Azure Resources',
    description: 'View Azure resource counts by type (uses your Microsoft login)',
    icon: Server,
    category: 'microsoft',
    defaultSize: { w: 4, h: 3, minW: 3, minH: 2, maxW: 8, maxH: 6 },
    requiresApi: true,
    configurable: false,
    helpText: 'Automatically uses your Microsoft credentials to show Azure resources.',
  },
  [WIDGET_TYPES.SECURITY_POSTURE_WIDGET]: {
    id: WIDGET_TYPES.SECURITY_POSTURE_WIDGET,
    name: 'Security Posture',
    description: 'View MFA coverage, Conditional Access policies, and risky sign-ins',
    icon: Shield,
    category: 'microsoft',
    defaultSize: { w: 4, h: 4, minW: 3, minH: 3, maxW: 8, maxH: 6 },
    requiresApi: true,
    configurable: false,
    helpText: 'Shows your organization\'s security posture including MFA and CA policy coverage.',
  },
  [WIDGET_TYPES.SERVICE_HEALTH_WIDGET]: {
    id: WIDGET_TYPES.SERVICE_HEALTH_WIDGET,
    name: 'Service Health',
    description: 'Monitor Microsoft 365 service health status',
    icon: Activity,
    category: 'microsoft',
    defaultSize: { w: 3, h: 4, minW: 2, minH: 3, maxW: 6, maxH: 8 },
    requiresApi: true,
    configurable: false,
    helpText: 'Real-time status of Microsoft 365 services like Teams, Exchange, and SharePoint.',
  },
  [WIDGET_TYPES.STORAGE_USAGE_WIDGET]: {
    id: WIDGET_TYPES.STORAGE_USAGE_WIDGET,
    name: 'Storage Usage',
    description: 'View SharePoint, OneDrive, and Exchange storage metrics',
    icon: HardDrive,
    category: 'microsoft',
    defaultSize: { w: 4, h: 4, minW: 3, minH: 3, maxW: 8, maxH: 6 },
    requiresApi: true,
    configurable: false,
    helpText: 'Storage utilization across your Microsoft 365 services.',
  },
  [WIDGET_TYPES.TENANT_INFO_WIDGET]: {
    id: WIDGET_TYPES.TENANT_INFO_WIDGET,
    name: 'Tenant Info',
    description: 'Display your Microsoft 365 tenant details',
    icon: Building,
    category: 'microsoft',
    defaultSize: { w: 3, h: 3, minW: 2, minH: 2, maxW: 6, maxH: 4 },
    requiresApi: true,
    configurable: false,
    helpText: 'Shows tenant name, domain, and basic organization information.',
  },

  // === IT OPERATIONS WIDGETS ===
  [WIDGET_TYPES.PROJECTS_WIDGET]: {
    id: WIDGET_TYPES.PROJECTS_WIDGET,
    name: 'IT Projects',
    description: 'Track IT projects with status, progress, and deadlines',
    icon: FolderKanban,
    category: 'it_operations',
    defaultSize: { w: 6, h: 5, minW: 4, minH: 3, maxW: 12, maxH: 8 },
    requiresApi: true,
    configurable: false,
    helpText: 'View and manage your IT project portfolio.',
  },
  [WIDGET_TYPES.INTERNAL_SERVICES_WIDGET]: {
    id: WIDGET_TYPES.INTERNAL_SERVICES_WIDGET,
    name: 'Internal Services',
    description: 'Monitor internal service health and uptime',
    icon: Server,
    category: 'it_operations',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3, maxW: 12, maxH: 8 },
    requiresApi: true,
    configurable: false,
    helpText: 'Real-time monitoring of your internal services with health checks.',
  },
  [WIDGET_TYPES.EXTERNAL_SERVICES_WIDGET]: {
    id: WIDGET_TYPES.EXTERNAL_SERVICES_WIDGET,
    name: 'External Services',
    description: 'Smartsheet, RingCentral, and other external service metrics',
    icon: Globe,
    category: 'it_operations',
    defaultSize: { w: 4, h: 3, minW: 3, minH: 2, maxW: 8, maxH: 6 },
    requiresApi: true,
    configurable: false,
    helpText: 'External service usage statistics from Smartsheet, RingCentral, etc.',
  },
  [WIDGET_TYPES.JIRA_OPERATIONS_WIDGET]: {
    id: WIDGET_TYPES.JIRA_OPERATIONS_WIDGET,
    name: 'Jira Operations',
    description: 'Jira tickets, SLA metrics, and satisfaction scores',
    icon: Ticket,
    category: 'it_operations',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3, maxW: 12, maxH: 8 },
    requiresApi: true,
    configurable: false,
    helpText: 'Service desk metrics from Jira Service Management.',
  },
  [WIDGET_TYPES.GOALS_TRACKER_WIDGET]: {
    id: WIDGET_TYPES.GOALS_TRACKER_WIDGET,
    name: '2026 Goals',
    description: 'Track progress on annual IT goals and initiatives',
    icon: Target,
    category: 'it_operations',
    defaultSize: { w: 4, h: 4, minW: 3, minH: 3, maxW: 8, maxH: 8 },
    requiresApi: false,
    configurable: false,
    helpText: 'Track your annual IT department goals and initiatives.',
  },
  [WIDGET_TYPES.EXPENSES_WIDGET]: {
    id: WIDGET_TYPES.EXPENSES_WIDGET,
    name: 'Additional Expenses',
    description: 'Track IT expenses and budget items',
    icon: DollarSign,
    category: 'it_operations',
    defaultSize: { w: 4, h: 3, minW: 3, minH: 2, maxW: 8, maxH: 6 },
    requiresApi: true,
    configurable: false,
    helpText: 'Track additional IT expenses and budget allocations.',
  },

  // === PRODUCTIVITY WIDGETS ===
  [WIDGET_TYPES.CUSTOM_CHECKLIST]: {
    id: WIDGET_TYPES.CUSTOM_CHECKLIST,
    name: 'Checklist',
    description: 'Create a simple to-do or checklist',
    icon: CheckSquare,
    category: 'productivity',
    defaultSize: { w: 3, h: 3, minW: 2, minH: 2, maxW: 6, maxH: 8 },
    requiresApi: false,
    configurable: true,
  },
  [WIDGET_TYPES.CUSTOM_COUNTDOWN]: {
    id: WIDGET_TYPES.CUSTOM_COUNTDOWN,
    name: 'Countdown Timer',
    description: 'Count down to an important date or deadline',
    icon: Calendar,
    category: 'productivity',
    defaultSize: { w: 2, h: 2, minW: 2, minH: 2, maxW: 4, maxH: 3 },
    requiresApi: false,
    configurable: true,
  },
  [WIDGET_TYPES.QUICK_LINKS]: {
    id: WIDGET_TYPES.QUICK_LINKS,
    name: 'Quick Links',
    description: 'Collection of clickable links to important resources',
    icon: Link2,
    category: 'productivity',
    defaultSize: { w: 3, h: 3, minW: 2, minH: 2, maxW: 6, maxH: 8 },
    requiresApi: false,
    configurable: true,
  },

  // === EMBED WIDGETS ===
  [WIDGET_TYPES.POWERBI_REPORT]: {
    id: WIDGET_TYPES.POWERBI_REPORT,
    name: 'Power BI Report',
    description: 'Embed a Power BI report or dashboard',
    icon: BarChart3,
    category: 'embeds',
    defaultSize: { w: 6, h: 4, minW: 4, minH: 3, maxW: 12, maxH: 8 },
    requiresApi: false,
    configurable: true,
    helpText: 'To get the embed URL: Open your report in Power BI service → Select File → Embed report → Website or portal → Copy the iframe code or secure link.',
  },
  [WIDGET_TYPES.EMBED_WEBSITE]: {
    id: WIDGET_TYPES.EMBED_WEBSITE,
    name: 'Embed Website',
    description: 'Embed any website or web app in an iframe',
    icon: Globe,
    category: 'embeds',
    defaultSize: { w: 6, h: 4, minW: 3, minH: 3, maxW: 12, maxH: 8 },
    requiresApi: false,
    configurable: true,
    helpText: 'Enter the URL of the website to embed. Note: Some websites may block iframe embedding.',
  },
  [WIDGET_TYPES.JIRA_DASHBOARD_SNAPSHOT]: {
    id: WIDGET_TYPES.JIRA_DASHBOARD_SNAPSHOT,
    name: 'Jira Dashboard Snapshot',
    description: 'Auto-refreshing PNG snapshot of a shared Jira dashboard via the Jira proxy',
    icon: Activity,
    category: 'it_operations',
    defaultSize: { w: 6, h: 5, minW: 3, minH: 3, maxW: 12, maxH: 12 },
    requiresApi: false,
    configurable: true,
    helpText: 'Enter the dashboard ID (e.g. 10114). The proxy regenerates the screenshot every 5 minutes.',
  },
};

// Widget categories for the catalog UI
export const WIDGET_CATEGORIES = [
  {
    id: 'microsoft',
    name: 'Microsoft & Azure',
    description: 'Auto-fetch data using your Microsoft login',
  },
  {
    id: 'it_operations',
    name: 'IT Operations',
    description: 'Projects, services, Jira, goals, and expenses',
  },
  {
    id: 'metrics',
    name: 'Metrics & KPIs',
    description: 'Track numbers, percentages, and custom APIs',
  },
  {
    id: 'content',
    name: 'Content',
    description: 'Text, notes, images, and information',
  },
  {
    id: 'productivity',
    name: 'Productivity',
    description: 'Checklists, countdowns, and quick links',
  },
  {
    id: 'embeds',
    name: 'Embeds & Reports',
    description: 'Power BI, websites, and external content',
  },
];

// Get widgets by category
export const getWidgetsByCategory = (categoryId) => {
  return Object.values(WIDGET_CATALOG).filter(w => w.category === categoryId);
};

// Default layout for new dashboards - empty, user builds their own
export const DEFAULT_IT_EXECUTIVE_LAYOUT = [];

// Helper to generate unique widget IDs
export const generateWidgetId = (type) => {
  return `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Create a new widget instance
export const createWidgetInstance = (type, position = { x: 0, y: 0 }) => {
  const config = WIDGET_CATALOG[type];
  if (!config) return null;

  return {
    i: generateWidgetId(type),
    type,
    x: position.x,
    y: position.y,
    w: config.defaultSize.w,
    h: config.defaultSize.h,
    minW: config.defaultSize.minW,
    minH: config.defaultSize.minH,
    maxW: config.defaultSize.maxW,
    maxH: config.defaultSize.maxH,
    config: {}, // Widget-specific configuration
  };
};
